// Library methods
import { useCallback, useMemo, useState } from "react";
// import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import WebRtc from "src/components/containers/shared/LiveUpdateModal/LiveUpdateContent/WebRtc/WebRtc";
import LensSimulationOptions from "./LensSimulationOptions/LensSimulationOptions";
import LensSelectionCard from "./LensSelectionCard/LensSelectionCard";
// import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Services
import { deviceUpdateLensParameter } from "src/services/signalR/api";

// Constants
import { DEFAULT_SIM_ENVIRONMENT, eyeTypeEnums } from "src/constants/enums";

// Utils
import {
  getLensForEye,
  isLensViewed,
  // isLensViewedByBothEyes,
} from "src/utils/exams/lensSimulationHelper";

// The fields deviceUpdateLensParameter's `data` accepts, minus lensName --
// derived from the function itself so this stays in sync with api.ts.
export type LensParameterUpdate = Partial<
  Omit<Parameters<typeof deviceUpdateLensParameter>[2], "lensName">
>;

type LensSimulationType = {
  examData: ExamSectionReportType | undefined;
  productType: string;
  connection: HubConnection | null;
};

const LensSimulation = ({
  examData,
  productType,
  connection,
}: LensSimulationType) => {
  // const { t } = useTranslation();
  const { device } = useLiveUpdateContext();
  const { getToken } = useTokenAccess();

  // IOL lenses can be chosen per eye; every other product type is both eyes.
  const isIOL = productType === "IOL";

  const currentSimEnv = useMemo(
    () => examData?.state?.simulationEnvironment ?? DEFAULT_SIM_ENVIRONMENT,
    [examData?.state?.simulationEnvironment]
  );

  const currentLens = useMemo(
    () =>
      examData?.state?.lenses
        ?.filter((lens) => lens?.productType === productType)
        ?.find((lens) => isLensViewed(lens)) ?? examData?.state?.lenses?.[0],
    [examData?.state?.lenses, productType]
  );

  const productLenses = useMemo(
    () =>
      examData?.state?.lenses?.filter(
        (lens) => lens?.productType === productType
      ) ?? [],
    [examData?.state?.lenses, productType]
  );

  const isDeviceReady = Boolean(device?.id && connection);
  const isCurrentLensActionDisabled = !isDeviceReady || !currentLens?.name;

  // The lens each eye is viewing, as the device reports it.
  const eyeLenses = useMemo(
    () =>
      Object.fromEntries(
        [eyeTypeEnums.LEFT, eyeTypeEnums.RIGHT].map((eye) => [
          eye,
          getLensForEye(productLenses, eye),
        ])
      ),
    [productLenses]
  );

  // Starts per eye when the device reports the eyes on different lenses (or
  // one eye with none).
  const [isIndividualEyes, setIsIndividualEyes] = useState(
    () =>
      isIOL &&
      eyeLenses[eyeTypeEnums.LEFT]?.name !== eyeLenses[eyeTypeEnums.RIGHT]?.name
  );

  // Every lens update goes through here, named by lens, so each eye can
  // update the lens it's viewing.
  const sendLensUpdate = useCallback(
    async (lensName: string, update: LensParameterUpdate) => {
      if (!device?.id || !connection) return;

      try {
        const token = await getToken();
        await deviceUpdateLensParameter(
          token,
          device.id,
          { lensName, ...update },
          connection
        );
      } catch (e) {
        console.error(e);
      }
    },
    [device?.id, connection, getToken]
  );

  // Going back to "Both Eyes" puts the first lens on both eyes
  // const handleSetIndividualEyes = useCallback(
  //   (individual: boolean) => {
  //     setIsIndividualEyes(individual);
  //     if (individual) return;

  //     const firstLens = productLenses[0];
  //     if (firstLens && !isLensViewedByBothEyes(firstLens)) {
  //       sendLensUpdate(firstLens.name, { currentlyBeingViewed: true });
  //     }
  //   },
  //   [productLenses, sendLensUpdate]
  // );

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      {/* {isIOL && (
        <Box display="flex" alignItems="center" gap="8px" maxWidth="100%">
          <Typography minWidth="fit-content" fontSize="12px">
            {t("Select lenses for")}:
          </Typography>
          <ExamCardButton
            label={t("Both Eyes")}
            isSelected={!isIndividualEyes}
            isDisabled={!isDeviceReady}
            updateValue={() => handleSetIndividualEyes(false)}
          />
          <ExamCardButton
            label={t("Individual Eyes")}
            isSelected={isIndividualEyes}
            isDisabled={!isDeviceReady}
            updateValue={() => handleSetIndividualEyes(true)}
          />
        </Box>
      )} */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr" },
          gap: "8px",
        }}
      >
        <Box sx={{ minHeight: "300px", maxHeight: "350px" }}>
          <WebRtc connection={connection} />
        </Box>
        {productLenses.length === 0 ? (
          <Typography sx={{ fontSize: "14px" }}>N/A</Typography>
        ) : isIndividualEyes ? null : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
              },
              gap: "8px",
            }}
          >
            {productLenses.map((lens) => (
              <LensSelectionCard
                key={lens.name}
                lens={lens}
                isDeviceReady={isDeviceReady}
                connection={connection}
              />
            ))}
          </Box>
        )}
        <LensSimulationOptions
          productType={productType}
          connection={connection}
          currentLens={currentLens}
          currentSimEnv={currentSimEnv}
          isCurrentLensActionDisabled={isCurrentLensActionDisabled}
          isDeviceReady={isDeviceReady}
          isIndividualEyes={isIndividualEyes}
          eyeLenses={eyeLenses}
          productLenses={productLenses}
          sendLensUpdate={sendLensUpdate}
        />
      </Box>
    </Box>
  );
};

export default LensSimulation;
